import { useAuthStore } from '@/stores/useAuthStore';
import { useThemeStore, type Theme } from '@/stores/themeStore';

// Pages de Compet intégrées en iframe par un site DartsHub partenaire (?embed=true, cf.
// CMER components/CompetEmbed.vue) : classements complets avec la session du site hôte,
// transmise par postMessage. Différent de /embed/classement/... (LeaderboardEmbed.vue) :
// version anonyme pour n'importe quel site tiers, sans session.
const HOST_ORIGINS = ['https://cmerdarts.fr'];

export const isAllowedHostOrigin = (origin: string) =>
  HOST_ORIGINS.includes(origin) || /^https?:\/\/localhost:\d+$/.test(origin);

export const isHostEmbed = new URLSearchParams(window.location.search).get('embed') === 'true'
  && window.parent !== window;

// Messages échangés avec le site hôte (hauteur : EMBED_HEIGHT_MESSAGE, cf. leaderboards.ts).
export const HOST_AUTH_MESSAGE = 'compet_auth';
export const HOST_LOGOUT_MESSAGE = 'compet_logout';
export const HOST_LOGIN_REQUEST_MESSAGE = 'compet_request_login';

// Thèmes du site hôte que Compet sait afficher (cf. styles/theme.css).
const HOST_THEMES: Theme[] = ['cmer', 'geek'];

let resolveReady: () => void;
export const authReady: Promise<void> = new Promise((resolve) => {
  resolveReady = resolve;
});

// Le site hôte envoie sa session une fois l'iframe chargée : la première navigation
// attend ce message (cf. router.beforeEach), sinon la page chargerait la version anonyme
// du classement. Sans message sous 500 ms (visiteur non connecté), on continue sans.
export function initHostEmbed() {
  if (!isHostEmbed) {
    resolveReady();
    return;
  }

  const theme = new URLSearchParams(window.location.search).get('theme') as Theme | null;
  if (theme && HOST_THEMES.includes(theme)) useThemeStore().setTheme(theme);

  // Seule la session du site hôte compte : celle restée dans le stockage de l'iframe
  // (connexion précédente, déconnexion faite pendant que l'iframe était fermée) est oubliée.
  const authStore = useAuthStore();
  if (authStore.token) authStore.logout();

  let ready = false;
  const timeout = setTimeout(() => {
    ready = true;
    resolveReady();
  }, 500);

  // Connexion / déconnexion sur le site hôte après le chargement : rechargement de
  // l'iframe, le site hôte renvoie sa session au chargement (même chemin que l'arrivée).
  window.addEventListener('message', (event: MessageEvent) => {
    if (!isAllowedHostOrigin(event.origin)) return;

    if (event.data?.type === HOST_LOGOUT_MESSAGE) {
      if (!authStore.isAuthenticated) return;
      authStore.logout();
      window.location.reload();
      return;
    }

    if (event.data?.type !== HOST_AUTH_MESSAGE || !event.data.token) return;
    const wasAuthenticated = authStore.isAuthenticated;
    authStore.setAuthData(event.data);

    if (!ready) {
      ready = true;
      clearTimeout(timeout);
      resolveReady();
      return;
    }
    if (!wasAuthenticated) window.location.reload();
  });
}
